// JsonExporter utility for dynamically exporting formatted JSON snapshots
// Directly writes/appends to FormatJson.js file (client-side, no backend needed)

const JsonExporter = (() => {
  let exportCounter = 0;

  /**
   * Get the current export counter
   */
  const getCounter = () => exportCounter;

  /**
   * Increment and return the next export name
   */
  const getNextExportName = () => {
    exportCounter++;
    return `FormatJson${exportCounter}`;
  };

  /**
   * Format data for export
   */
  const formatExportData = (data) => {
    return JSON.stringify(data, null, 2);
  };

  /**
   * Generate export statement as a string
   */
  const generateExportStatement = (exportName, data) => {
    return `export const ${exportName} = ${formatExportData(data)};\n\n`;
  };

  /**
   * Export data directly to FormatJson.js
   * Generates the code and provides options to append it
   */
  const exportToFile = async (data, user) => {
    try {
      const exportName = getNextExportName();
      const exportStatement = generateExportStatement(exportName, data);

      // Log to console for easy copy-paste
      console.log(`✓ Ready to export as: ${exportName}`);
      console.log(
        `Copy this code and paste it into FormatJson.js:\n\n${exportStatement}`,
      );

      // Store in localStorage as backup
      const exports = JSON.parse(
        localStorage.getItem("formatJsonExports") || "{}",
      );
      exports[exportName] = data;
      localStorage.setItem("formatJsonExports", JSON.stringify(exports));

      // Create downloadable file option
      const fullContent = Object.entries(exports)
        .map(([name, exportData]) => generateExportStatement(name, exportData))
        .join("");

      const blob = new Blob([fullContent], { type: "text/javascript" });
      const downloadUrl = URL.createObjectURL(blob);

      console.log(`📥 Download FormatJson.js: ${downloadUrl}`);
      console.log(
        `📍 Or manually copy the export above and paste into src/js/utils/FormatJson.js`,
      );

      return {
        success: true,
        exportName,
        exportStatement,
        downloadUrl,
      };
    } catch (error) {
      console.error("Error generating export:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Get all stored exports from localStorage
   */
  const getAllExports = () => {
    return JSON.parse(localStorage.getItem("formatJsonExports") || "{}");
  };

  /**
   * Generate complete FormatJson.js content with all exports
   */
  const generateCompleteFile = () => {
    const exports = getAllExports();
    let content = "// Dynamic JSON exports from MetaSearchFilter\n";
    content += "// Auto-generated by JsonExporter utility\n\n";

    Object.entries(exports).forEach(([name, data]) => {
      content += generateExportStatement(name, data);
    });

    return content;
  };

  /**
   * Download the complete FormatJson.js file
   */
  const downloadCompleteFile = () => {
    const content = generateCompleteFile();
    const blob = new Blob([content], { type: "text/javascript" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "FormatJson.js";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   * Reset counter and storage (for development)
   */
  const reset = () => {
    exportCounter = 0;
    localStorage.removeItem("formatJsonExports");
  };

  return {
    exportToFile,
    getAllExports,
    generateCompleteFile,
    downloadCompleteFile,
    getCounter,
    getNextExportName,
    generateExportStatement,
    reset,
  };
})();

export default JsonExporter;
